import type { DashboardMapItem } from '@/core/interfaces/Dashboard';

/** Vietnam mainland bounding box used for validation and fitBounds. */
export const VIETNAM_BOUNDS = {
  minLat: 8.0,
  maxLat: 24.0,
  minLng: 102.0,
  maxLng: 110.0,
};

/** Slightly tighter mainland clip — excludes Hoàng Sa / Trường Sa outliers. */
export const VIETNAM_MAINLAND_CLIP = {
  minLat: 8.3,
  maxLat: 23.5,
  minLng: 102.1,
  maxLng: 109.5,
};

export type LngLat = { longitude: number; latitude: number };

/**
 * GeoJSON / MapLibre order is [longitude, latitude].
 * Detect and correct clearly swapped pairs (common API mistake).
 */
export function normalizeLngLat(
  longitude: number | null | undefined,
  latitude: number | null | undefined
): LngLat | null {
  const lng = Number(longitude);
  const lat = Number(latitude);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }
  if (lng === 0 && lat === 0) {
    return null;
  }

  const lngLooksLikeLat =
    lat >= VIETNAM_BOUNDS.minLng &&
    lat <= VIETNAM_BOUNDS.maxLng &&
    lng >= VIETNAM_BOUNDS.minLat &&
    lng <= VIETNAM_BOUNDS.maxLat;

  const lngLooksValid =
    lng >= VIETNAM_BOUNDS.minLng && lng <= VIETNAM_BOUNDS.maxLng;
  const latLooksValid =
    lat >= VIETNAM_BOUNDS.minLat && lat <= VIETNAM_BOUNDS.maxLat;

  if (!lngLooksValid && !latLooksValid && lngLooksLikeLat) {
    return { longitude: lat, latitude: lng };
  }

  if (!lngLooksValid && latLooksValid && lngLooksLikeLat) {
    return { longitude: lat, latitude: lng };
  }

  if (lngLooksValid && latLooksValid) {
    return { longitude: lng, latitude: lat };
  }

  if (lngLooksLikeLat) {
    return { longitude: lat, latitude: lng };
  }

  return null;
}

export function isValidMapCoordinate(
  latitude: number | null | undefined,
  longitude: number | null | undefined
): boolean {
  return normalizeLngLat(longitude, latitude) != null;
}

/** Only render markers with saved, valid coordinates — never guess on the frontend. */
export function filterLocatedMarkers(
  markers: DashboardMapItem[]
): DashboardMapItem[] {
  const normalizedMarkers = markers
    .map((marker) => {
      const normalized = normalizeLngLat(marker.longitude, marker.latitude);
      if (!normalized) {
        return null;
      }
      return {
        ...marker,
        longitude: normalized.longitude,
        latitude: normalized.latitude,
      };
    })
    .filter((marker): marker is DashboardMapItem => marker != null);

  return separateCoincidentMarkers(normalizedMarkers);
}

/**
 * Nudge exact duplicate coordinates (~35–80 m) so stacked warehouses remain visible.
 * Does not invent locations for invalid/missing coords.
 */
function separateCoincidentMarkers(
  markers: DashboardMapItem[]
): DashboardMapItem[] {
  const groups = new Map<string, number>();

  return markers.map((marker) => {
    const key = `${marker.longitude.toFixed(5)},${marker.latitude.toFixed(5)}`;
    const index = groups.get(key) ?? 0;
    groups.set(key, index + 1);

    if (index === 0) {
      return marker;
    }

    const angle = (index * 2.399963) % (Math.PI * 2);
    const radiusDeg = 0.00035 * Math.ceil(index / 6);
    return {
      ...marker,
      longitude: marker.longitude + Math.cos(angle) * radiusDeg,
      latitude: marker.latitude + Math.sin(angle) * radiusDeg,
    };
  });
}

/**
 * Normalize fitBounds corners to [[west, south], [east, north]] in lng/lat order.
 */
export function normalizeFitBounds(
  bounds: [[number, number], [number, number]] | null | undefined
): [[number, number], [number, number]] | null {
  if (!bounds || bounds.length !== 2) {
    return null;
  }

  const a = normalizeLngLat(bounds[0][0], bounds[0][1]);
  const b = normalizeLngLat(bounds[1][0], bounds[1][1]);

  // Also try interpreting each corner as [lat, lng] if normalize failed.
  const aAlt = a ?? normalizeLngLat(bounds[0][1], bounds[0][0]);
  const bAlt = b ?? normalizeLngLat(bounds[1][1], bounds[1][0]);

  if (!aAlt || !bAlt) {
    return null;
  }

  const west = Math.min(aAlt.longitude, bAlt.longitude);
  const east = Math.max(aAlt.longitude, bAlt.longitude);
  const south = Math.min(aAlt.latitude, bAlt.latitude);
  const north = Math.max(aAlt.latitude, bAlt.latitude);

  if (east - west < 0.0001 && north - south < 0.0001) {
    const pad = 0.08;
    return [
      [west - pad, south - pad],
      [east + pad, north + pad],
    ];
  }

  if (
    west < VIETNAM_BOUNDS.minLng - 1 ||
    east > VIETNAM_BOUNDS.maxLng + 2 ||
    south < VIETNAM_BOUNDS.minLat - 1 ||
    north > VIETNAM_BOUNDS.maxLat + 1
  ) {
    return null;
  }

  return [
    [west, south],
    [east, north],
  ];
}

export function getMapCenterCoordinates(center: {
  latitude?: number;
  longitude?: number;
  lat?: number;
  lng?: number;
}): { latitude: number; longitude: number } {
  const normalized = normalizeLngLat(
    center.longitude ?? center.lng,
    center.latitude ?? center.lat
  );

  if (normalized) {
    return normalized;
  }

  return {
    latitude: 16.047079,
    longitude: 108.20623,
  };
}

/** True when a ring centroid sits inside mainland Vietnam. */
export function isMainlandCoordinate(lng: number, lat: number): boolean {
  return (
    lng >= VIETNAM_MAINLAND_CLIP.minLng &&
    lng <= VIETNAM_MAINLAND_CLIP.maxLng &&
    lat >= VIETNAM_MAINLAND_CLIP.minLat &&
    lat <= VIETNAM_MAINLAND_CLIP.maxLat
  );
}
